import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import Sheet from '../../models/Sheet'
import { currencyFormatter } from '../../utils/functions'
import BackgroundCarousel from '../BackgroundCarousel/BackgroundCarousel'
import Button from '../Button'
import DroppableArea from '../droppable-area'
import Image from '../Image/Image'
import Typography from '../Typography'
import classes from './package-builder-helper.module.sass'
import useBreakpoints from '../../hooks/use-breakpoints'
import FavoritePoseItem from '../favorite-pose-item'
import AddonSheet from '../../models/AddonSheet'
import useDeepCompareEffect from 'use-deep-compare-effect';

const PackageBuilderHelper = ({
  seniors = false,
  placeholder,
  addons = false,
  studentImages,
  backgrounds,
  selectedPackage,
  selectedOption,
  sheetList,
  onComplete = () => { },
  onSheetComplete,
  onBack,
  poses = false,
  prefilledSheets = [],
  initialIndex = 0,
  checkForeground = true,
  checkBackgrounds = true,
  className = '',
  maxPoses = 0,
  forwardText = 'Next',
  backText = 'Back',
  checkMaxPoses = true,
  editable = true,
  foregroundMsg = 'Drag pose to the sheet to build package.',
}) => {
  const [left, setLeft] = useState(0)
  const [top, setTop] = useState(0)
  const [checkBounds, setCheckBounds] = useState(false)
  const [selectedImg, setSelectedImg] = useState(null)
  const [bg, setBg] = useState(null)
  const [sheets, setSheets] = useState(prefilledSheets)
  const [index, setIndex] = useState(initialIndex)
  const [isNextDisabled, setIsNextDisabled] = useState(true)
  const breakpoint = useBreakpoints()
  const [selectedPoses, setSelectedPoses] = useState([])
  const [mouseEvent, setMouseEvent] = useState()

  useDeepCompareEffect(() => {
    setSheets(prefilledSheets);
  }, [prefilledSheets]);

  const pageMouseDown = (e) => {
    e.preventDefault();
    if (e.target.tagName === 'IMG') {
      // document.body.classList.add(classes.overflowHidden)
    }

    if (e.clientX && e.clientY) {
      setLeft(e.pageX)
      setTop(e.pageY)
      setMouseEvent(e)
    } else {
      setLeft(e.touches[0].pageX)
      setTop(e.touches[0].pageY)
      setMouseEvent(e)
    }
  }

  const onMouseDown = (image) => {
    setSelectedImg(image)
  }
  const onMouseUp = () => {
    setTimeout(() => {
      document.body.classList.remove(classes.overflowHidden)
    }, 250)
    setCheckBounds(true)
  }

  // need to most likely change this to update the sheet at the index when selecting each foreground
  // need to figure out a way to see if updating the current foreground would keep the length
  // the same or if it would increase the length
  const onForegroundSelection = (newForeground, sheetIndex) => {
    if (!checkMaxPoses) return true
    if (selectedPoses.includes(newForeground.id)) return true

    sheets[sheetIndex].seniorImageId = newForeground.id
    const nextState = [...new Set(sheets.map((sheet) => (sheet.seniorImageId || sheet.poseId)).filter((id) => !!id))]

    if (nextState.length > maxPoses) {
      const addAdditionalPose = confirm(`Would you like to add an additional pose for ${currencyFormatter(selectedOption.additionalPoseCharge || 0)}?`)

      if (!addAdditionalPose) return false
    }

    setSelectedPoses(nextState)

    return true
  }

  const offsetSum = sheetList
    .slice(0, index)
    .map((imageType) => imageType.count)
    .reduce((cur, acc) => cur + acc, 0)

  const offset = index - 1 >= 0 ? offsetSum : 0

  useEffect(() => {
    if (!sheetList) return

    // get length of sheets to check
    const length = sheetList
      .slice(0, index + 1)
      .map((imageType) => imageType.count)
      .reduce((cur, acc) => cur + acc, 0)

    // check if they all have a foreground and background
    const nextState = sheets
      .slice(0, length)
      .some((sheet) => !((!checkBackgrounds || sheet.backgroundId) && (!checkForeground || (sheet.seniorImageId || sheet.poseId))))

    setIsNextDisabled(nextState)
  }, [index, JSON.stringify(sheets)])

  useEffect(() => {
    if (!checkBounds) return

    setBg(null)
    setSelectedImg(null)
    setCheckBounds(false)
  }, [checkBounds])

  useDeepCompareEffect(() => {
    if (prefilledSheets.length > 0) return

    if (sheetList && sheetList.length > 0) {
      const totalLength = sheetList
        .map((imageType) => imageType.count)
        .reduce((cur, sum) => cur + sum)

      const Class = addons ? AddonSheet : Sheet
      const prefillPose = studentImages.length === 1

      // TODO: add logic for checking for seniorImage
      const poseIdKey = seniors ? 'seniorImageId' : 'poseId'
      const poseKey = seniors ? 'seniorImage' : 'pose'
      const attrs = prefillPose ? { [poseIdKey]: studentImages[0].id, [poseKey]: studentImages[0] } : {}

      setSheets(new Array(totalLength).fill(0).map((_, i) => new Class({ index: i, ...attrs })))
    }
  }, [sheetList])

  useEffect(() => {
    if (sheetList[index]) return

    onComplete(sheets)
  }, [index])

  const backHandler = () => {
    if (onBack) onBack()
    else setIndex(Math.max(0, index - 1))
  }

  const imageTypeClass = sheetList[index] ? sheetList[index].classname : ''

  return (
    <div
      id="scrollable"
      className={cn(classes.container, className)}
      onMouseDown={pageMouseDown}
      onMouseMove={pageMouseDown}
      onMouseUp={onMouseUp}
      onTouchStart={pageMouseDown}
      onTouchMove={pageMouseDown}
      onTouchEnd={onMouseUp}
    >
      <div className={classes.bgContainer}>
        {['sm', 'md'].includes(breakpoint) ? (
          <>
            {checkForeground && (
              <BackgroundCarousel
                small
                scrollable={false}
                onMouseDown={onMouseDown}
                message={foregroundMsg}
                highlight={false}
                backgrounds={studentImages}
              />
            )}
            {checkBackgrounds && (
              <BackgroundCarousel
                small
                onMouseDown={(newBg) => setBg(newBg)}
                message="Drag background to each sheet."
                highlight={false}
                backgrounds={backgrounds}
              />
            )}
          </>
        ) : (
          <>
            {checkForeground && !selectedOption.digitalImagesOnly && (
              <div>
                <Typography classes={classes.title} weight="bold">{foregroundMsg}</Typography>
                <div className={cn(classes.flexContainer, classes.studentImagesContainer)}>
                  {studentImages.map((studentImage) => (
                    <div key={studentImage.id} className={classes.flexItem}>
                      <FavoritePoseItem
                        displayBottom={false}
                        disabled
                        favoriteDefault
                        id={studentImage.id}
                        fileName={studentImage.imageFileName}
                        foreground={studentImage.watermarkUrl}
                        background={backgrounds[0].imageUrl}
                        onFavoriteClick={() => { }}
                        onImageClick={() => onMouseDown(studentImage)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {checkBackgrounds && (
              <div>
                <Typography classes={classes.title} weight="bold">Drag background to each sheet.</Typography>
                <div className={classes.flexContainer}>
                  {backgrounds.map((background) => (
                    <div key={background.id} className={classes.flexItem}>
                      <div
                        onMouseDown={() => setBg(background)}
                      >
                        <Image
                          src={background.imageUrl}
                          alt={`Background ${background.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {selectedImg && (
          <div className={classes.moveable} style={{ left, top }}>
            <Image src={selectedImg.watermarkUrl} alt="draggable-img" />
          </div>
        )}
        {bg && (
          <div className={classes.moveable} style={{ left, top }}>
            <Image src={bg.imageUrl} alt="draggable-img" />
          </div>
        )}
      </div>
      <div className={classes.droppableSection}>
        <Typography classes={classes.title} align="center" weight="bold">
          {index < sheetList.length && (
            <span>{`Sheet ${index + 1} of ${sheetList.length}: `}</span>
          )}
          {sheetList[index] ? sheetList[index].name : ''}
        </Typography>
        <div className={cn(classes.droppableContainer, classes[imageTypeClass])}>
          {sheetList[index] && (
            sheets.slice(offset, sheetList[index].count + offset).map((sheet) => (
              <DroppableArea
                key={`${sheet.id} ${sheet.index} ${sheet.poseId} ${sheet.backgroundId}`}
                checkForeground={checkForeground}
                placeholder={placeholder}
                addons={addons}
                onForegroundSelection={onForegroundSelection}
                newForeground={checkForeground ? selectedImg : placeholder}
                newBackground={bg}
                checkBounds={checkBounds}
                imageType={sheetList[index]}
                mouseCoordinates={{ x: left, y: top }}
                prefillSheet={sheet}
                setSheets={setSheets}
                poses={poses}
                onSheetComplete={onSheetComplete}
                index={sheet.index}
                isBgRequired={checkBackgrounds}
                mouseEvent={mouseEvent}
                editable={editable}
              />
            )))}
        </div>
        <div
          className={cn({
            [classes.buttonsContainer]: true,
            [classes.oneItem]: !sheets[index],
          })}
        >
          <Button
            rounded
            variant="dark"
            disabled={!onBack && index === 0}
            onClick={backHandler}
          >
            {backText}
          </Button>
          {sheets[index] && (
            <Button
              rounded
              variant="primary"
              disabled={isNextDisabled}
              onClick={() => setIndex(index + 1)}
            >
              {forwardText}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PackageBuilderHelper
